import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { KlevuFetch, KlevuRecord, KlevuTypeOfRecord, kmcRecommendation } from '@klevu/core';
import Slider from './Slider';
import { useKlevuSetup } from './klevuSetup';

interface KlevuSmartSliderTab {
  Title: string;
  KlevuTabId: string;
}

interface CategoryConfig {
  id: string;
  label: string;
  recommendationId: string;
}

interface SmartRecommendationSliderProps {
  tabs: KlevuSmartSliderTab[] | null | undefined;
}

export default function KlevuSmartSlider({ tabs }: SmartRecommendationSliderProps) {
  if (!tabs || tabs.length === 0) {
    return null;
  }

  const categories = useMemo<CategoryConfig[]>(() => {
    return tabs
      .filter((tab) => tab && tab.Title && tab.KlevuTabId)
      .map((tab, index) => ({
        id: `tab-${index}`,
        label: tab.Title,
        recommendationId: tab.KlevuTabId,
      }));
  }, [tabs]);

  const [categoryProducts, setCategoryProducts] = useState<Map<string, KlevuRecord[]>>(new Map());
  const [checkedCategories, setCheckedCategories] = useState<Set<string>>(new Set());
  const [activeCategory, setActiveCategory] = useState<string>(categories.length > 0 ? categories[0].id : '');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const hasUserSelected = useRef(false);
  const { favourites, isKlevuReady } = useKlevuSetup();

  const fetchSmartRecommendationProducts = useCallback(async (category: CategoryConfig) => {
    if (!isKlevuReady) return;
    
    // Skip if already checked
    if (checkedCategories.has(category.id)) {
      return;
    }
    
    try {
      setIsLoading(true);

      const result = await KlevuFetch(
        kmcRecommendation(category.recommendationId, { 
          id: category.id,
          typeOfRecords: [KlevuTypeOfRecord.Product],
          visibilityGroupID: window._THULEDATA.SitecoreLanguage,
          priceFieldSuffix: `${window._THULEDATA.currencyCode}-${window._THULEDATA.SitecoreLanguage}`,
        })
      );
      
      const responseObject = result.queriesById(category.id);
      const records = responseObject?.records || [];
      
      setCategoryProducts(prev => {
        const newMap = new Map(prev);
        newMap.set(category.id, records);
        return newMap;
      });
      
      setCheckedCategories(prev => new Set(prev).add(category.id));
    } catch (error) {
      console.error('Failed to fetch smart recommendation products:', error);
      setCategoryProducts(prev => {
        const newMap = new Map(prev);
        newMap.set(category.id, []);
        return newMap;
      });
      setCheckedCategories(prev => new Set(prev).add(category.id));
    } finally {
      setIsLoading(false);
    }
  }, [isKlevuReady, checkedCategories]);

  // Filter categories that have products
  const categoriesWithProducts = useMemo(() => {
    return categories.filter(cat => {
      const products = categoryProducts.get(cat.id);
      return products && products.length > 0;
    });
  }, [categories, categoryProducts]);

  // Auto-select the first category (in original order) that has products.
  // Keep updating until the user manually clicks a tab.
  useEffect(() => {
    if (categoriesWithProducts.length === 0) {
      return;
    }

    if (!hasUserSelected.current) {
      // Always pick the first category in original order that has products
      const firstWithProducts = categoriesWithProducts[0];
      if (firstWithProducts && firstWithProducts.id !== activeCategory) {
        setActiveCategory(firstWithProducts.id);
      }
    } else {
      // User has picked a tab — only switch if their selection has no products
      const currentProducts = categoryProducts.get(activeCategory);
      if (!currentProducts || currentProducts.length === 0) {
        const firstWithProducts = categoriesWithProducts[0];
        if (firstWithProducts) {
          setActiveCategory(firstWithProducts.id);
        }
      }
    }
  }, [categoriesWithProducts, categoryProducts, activeCategory]);

  const handleCategoryClick = useCallback((categoryId: string) => {
    if (categoryId === activeCategory) return;
    hasUserSelected.current = true;
    setActiveCategory(categoryId);
  }, [activeCategory]);

  useEffect(() => {
    if (!window._KlevuConfig?.url || !window._KlevuConfig?.apiKey || !isKlevuReady) {
      return;
    }

    if (categories.length === 0) {
      return;
    }

    // Small delay to ensure config is fully initialized
    setTimeout(() => {
      // Fetch products for all categories in parallel
      const fetchPromises = categories.map(category => 
        fetchSmartRecommendationProducts(category)
      );
      Promise.all(fetchPromises);
    }, 0);
  }, [categories, isKlevuReady, fetchSmartRecommendationProducts]);

  // Get products for active category
  const activeProducts = useMemo(() => {
    return categoryProducts.get(activeCategory) || [];
  }, [categoryProducts, activeCategory]);

  // Hide component if no categories have products
  if (categoriesWithProducts.length === 0) {
    return null;
  }

  // If only one tab, display title instead of tabs
  const isSingleTab = categoriesWithProducts.length === 1;
  const singleTabTitle = isSingleTab ? categoriesWithProducts[0].label : undefined;
  
  const categoryButtons = isSingleTab ? undefined : (
    <div className="tabMenu scrollBar--0">
      {categoriesWithProducts.map((category) => (
        <button
          key={category.id}
          onClick={() => handleCategoryClick(category.id)}
          className={`btn paragraph tabMenuButton heading6 ${activeCategory === category.id ? 'active' : ''}`}
          disabled={isLoading}
        >
          {category.label}
        </button>
      ))}
    </div>
  );

  return (
      <Slider 
        products={activeProducts} 
        favourites={favourites} 
        title={singleTabTitle}
        categoryButtons={categoryButtons}
        variant='without-offset-margins'
      />
  );
}
